import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from './api'

interface AsyncState<T> {
  data: T | null
  loading: boolean
  error: string | null
}

/**
 * Minimal data-fetching hook. Tracks a request id so a slow response that
 * resolves after a newer one can never overwrite fresher data.
 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []) {
  const [state, setState] = useState<AsyncState<T>>({ data: null, loading: true, error: null })
  const requestId = useRef(0)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const run = useCallback(async () => {
    const id = ++requestId.current
    setState((current) => ({ ...current, loading: true, error: null }))
    try {
      const data = await fn()
      if (id !== requestId.current || !mounted.current) return
      setState({ data, loading: false, error: null })
    } catch (error) {
      if (id !== requestId.current || !mounted.current) return
      const message =
        error instanceof ApiError ? error.message : 'Something went wrong loading this page.'
      setState({ data: null, loading: false, error: message })
    }
    // `fn` is intentionally excluded — callers pass an inline closure, and the
    // explicit deps array is the contract for when to refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  useEffect(() => {
    void run()
  }, [run])

  return { ...state, reload: run, setData: (data: T) => setState({ data, loading: false, error: null }) }
}
